import React, { useState, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  IconButton,
  useTheme,
  Drawer,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Feed as FeedIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Campaign as CampaignIcon,
  Notifications as NotificationsIcon,
  ExpandLess,
  ExpandMore,
  Message as MessageIcon,
  Send as SendIcon,
  TravelExplore as TravelExploreIcon,
  Handshake as HandshakeIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  ManageAccounts as AccountIcon,
  Lock as PrivacyIcon,
  CreditCard as BillingIcon,
  Article as WorksIcon,
  Science as LabIcon,
  Interests as InterestsIcon,
} from "@mui/icons-material";
import useSWR from "swr";
import { fetcher } from "shared/helpers/axios";
import { useMyLabs } from "shared/hooks/useMyLabs";
import { useNewCounts } from "shared/hooks/useNewCounts";
import { useLocation, Link } from "react-router-dom";
import { getActivityClassName } from "shared/styles/enhancedTheme";

import { useNotifications } from "components/comm/notifications/contexts/NotificationContext";
import { useUserConsortia } from "shared/hooks/useUserConsortia";
import { useUserSuggestions } from "shared/hooks/useUserSuggestions";
import { useUser } from "shared/helpers/axios";
import { useUserStatus, USER_STATUSES, getStatusMeta } from "shared/contexts/UserStatusContext";
import Badge from "@mui/material/Badge";

// Count chips beside the links: as tall as the link text (12.5px × 1.3), the width the number needs
const COUNT_CHIP_SX = {
  ml: 'auto', mr: 0.5, height: 16, minWidth: 26, flexShrink: 0, fontSize: 9, fontWeight: 600,
  '& .MuiChip-label': { px: 0.75, lineHeight: 1 },
};

export const drawerWidth = 260;
export const collapsedDrawerWidth = 60;

export const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingTop: '64px',
    // zIndex: -1,
    // background: theme.palette.sidebar?.background || 'linear-gradient(135deg, #f8faff 0%, #f1f5f9 100%)',
    // background: open ? 'rgba(99, 101, 241, 0.1)' : 'transparent',
    background: 'rgba(99, 101, 241, 0.05)',
    borderRight: `1px solid ${theme.palette.sidebar?.border || 'rgba(99, 102, 241, 0.1)'}`,
    color: theme.palette.sidebar?.text || '#475569',
    boxShadow: '2px 0 10px rgba(99, 102, 241, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: open ? 'flex-start' : 'center', // Center align when collapsed
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(99, 102, 241, 0.3)',
      borderRadius: '3px',
    },
  },
}));

export const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'activitytype',
})(({ theme, open, active }) => ({
  margin: open ? '6px auto' : '4px auto',
  width: open ? '90%' : `${collapsedDrawerWidth - 8}px`,
  maxWidth: open ? 'none' : `${collapsedDrawerWidth - 8}px`,
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: open ? 'row' : 'column',
  padding: open ? '6px 16px' : '8px 2px',
  minHeight: open ? '40px' : '48px',
  overflow: 'hidden',
  backgroundColor: active 
    ? (theme.palette.sidebar?.active || 'rgba(99, 102, 241, 0.12)') 
    : 'transparent',
  backdropFilter: active ? 'blur(10px)' : 'none',
  border: active 
    ? `1px solid ${theme.palette.sidebar?.border || 'rgba(99, 102, 241, 0.2)'}` 
    : '1px solid transparent',
  '&:hover': {
    backgroundColor: theme.palette.sidebar?.hover || 'rgba(99, 102, 241, 0.08)',
    transform: open ? 'translateX(2px)' : 'scale(1.05)',
    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.15)',
  },
  '& .MuiListItemIcon-root': {
    color: theme.palette.sidebar?.text || '#475569',
    minWidth: open ? '20px' : 'unset',
    marginRight: open ? theme.spacing(0.5) : 0,
    marginBottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiListItemText-primary': {
    ...theme.typography.sidebarItem,
    // fontWeight: active ? 600 : 500,
    color: theme.palette.sidebar?.text || '#475569',
    textAlign: open ? 'left' : 'center', // Center text when collapsed
    // fontSize: open ? '1.15rem' : '1.2rem', // Smaller text when collapsed
    margin: 0,
    padding: 0,
  },
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.sidebarSection,
  color: theme.palette.sidebar?.textSecondary || '#64748b',
}));

export const StyledDrawerContainer = styled(Box)(({ theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: open ? 'flex-start' : 'center',
  width: '100%',
  flex: 1,
  paddingTop: theme.spacing(2),
  paddingBottom: 0,
}));

const Sidebar = ({ open, onToggle }) => {
  const [expandedSections, setExpandedSections] = useState({
    main: true,
    research: true,
    collaboration: true,
  });
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);

  const location = useLocation();

  const { user } = useUser();
  const { status: myStatus, setStatus: setMyStatus } = useUserStatus();
  const myStatusMeta = getStatusMeta(myStatus);

  const theme = useTheme();
  
  const { 
    unreadCount, 
  } = useNotifications();

  const { count: userConsortiaCount } = useUserConsortia();
  const { receivedCount: userSuggestionsCount } = useUserSuggestions();
  // the labs you lead or belong to: one link each (at most three)
  const myLabs = useMyLabs(Boolean(user?.id)).slice(0, 3);
  const newCounts = useNewCounts(Boolean(user?.id));       // relevant items added since the last visit
  // how many works you write (the same list as My Works, so they share one request)
  const { data: worksData } = useSWR(user?.id ? '/proposal/proposals/?page_size=500' : null, fetcher, { revalidateOnFocus: false });
  const worksCount = (Array.isArray(worksData) ? worksData : worksData?.results || []).filter((w) => !w.you_left).length;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Derive a simple avatar for the Feed link (first letter of username or fallback)
  const feedAvatar = (
    <Avatar
      src={user?.avatar}
      sx={{
        width: 30,
        height: 30,
        bgcolor: theme.palette.primary.light,
        fontSize: 14,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      alt={user?.username || 'User'}
    >
      {!user?.avatar && (user?.username?.[0] || 'U').toUpperCase()}
    </Avatar>
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navigationItems = useMemo(() => {
    const items = [
      {
        title: "",
        key: "feed",
        activityType: "feed",
        expandable: false,
        items: [
          {
            icon: <FeedIcon /> ,
            text: "Activity Feed",
            path: `/feed/${user?.id}/`,
            // activityType: "profile",
          },
        ],
      },
      {
        title: "Main",
        key: "main",
        activityType: "profile",
        expandable: true,
        items: [
          {
            icon: <PersonIcon />,
            text: "Profile",
            path: `/profile/${user?.id}/`,
            // activityType: "profile",
          },     
          {
            icon: <GroupIcon />,
            text: "Consortium",
            path: "/consortium/",
            badge: userConsortiaCount,
            // activityType: "collaboration",
          },

          {
            icon: <WorksIcon />,
            text: "Works",
            path: "/works/",
            badge: worksCount,
          },
    
          // the labs you belong to, each by its name (none: no link)
          ...myLabs.map((lab) => ({
            icon: <LabIcon />,
            text: lab.name,
            path: `/lab/${lab.id}/`,
          })),
        ],
      },
      {
        title: "Collaboration",
        key: "research",
        expandable: true,
        activityType: "research",
        items: [
          {
            icon: <CampaignIcon />,
            text: "Funding Calls",
            path: "/funding/",
            newBadge: newCounts.funding,
            newLabel: "new calls matching your profile",
            // activityType: "funding",
          },
          {
            icon: <TravelExploreIcon />,
            text: "Collaboration Hub",
            path: "/hub/",
          },
          {
            icon: <InterestsIcon />,
            text: "Interests",
            path: "/interests/",
            newBadge: newCounts.interests,
            newLabel: "new interests on calls for you, and announcements",
          },
          {
            icon: <EventIcon />,
            text: "Events",
            path: "/events/",
          },
        ],
      },
      {
        title: "Communication",
        key: "collaboration",
        expandable: true,
        activityType: "collaboration",
        items: [
          {
            icon: <NotificationsIcon />,
            text: "Notifications",
            path: "/notifications",
            badge: unreadCount,
            // activityType: "collaboration",
          },
          {
            icon: <SendIcon />,
            text: "Suggestions",
            path: "/suggestions/",
            badge: userSuggestionsCount,
            // activityType: "collaboration",
          },
          {
            icon: <MessageIcon />,
            text: "Messages",
            path: "/messages/",
            // activityType: "collaboration",
          },
        ],
      },
    ];

    return items;
  }, [user?.id, unreadCount, userConsortiaCount, userSuggestionsCount, feedAvatar, myLabs, worksCount, newCounts]);

  const renderNavigationItem = (item) => (
    <StyledListItem
      key={item.path}
      button
      component={Link}
      to={item.path}
      data-guide={`nav-${item.text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}   /* page guides point at links */
      open={open}
      active={isActive(item.path)}
      activitytype={item.activityType}
      className={getActivityClassName(item.activityType)}
      sx={{ textDecoration: "none", color: "inherit" }}
    >
      <ListItemIcon sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {item.icon}
      </ListItemIcon>
      {open && <ListItemText primary={item.text} sx={{ ml: 0 }} />}
      {/* totals: an outlined chip as tall as the link text; new since the last visit: a filled one */}
      {open && item.badge > 0 && !(item.newBadge > 0) && (
        <Chip label={item.badge} size="small" variant="outlined" sx={{ ...COUNT_CHIP_SX,
          color: 'rgba(163, 163, 163, 1)', borderColor: 'rgba(163, 163, 163, .75)' }} />
      )}
      {open && item.newBadge > 0 && (
        <Tooltip title={`${item.newBadge} ${item.newLabel || 'new'} since your last visit`} placement="right">
          <Chip label={item.newBadge > 999 ? '999+' : item.newBadge} size="small" color="primary"
            sx={{ ...COUNT_CHIP_SX, fontWeight: 700 }} />
        </Tooltip>
      )}
      {!open && item.text && (
        <>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.7rem',
              textAlign: 'center',
              lineHeight: 1.1,
              mt: 0.7,
              px: 0,
              maxWidth: `${collapsedDrawerWidth - 12}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.text}
          </Typography>
          <Tooltip title={item.text} placement="right" arrow>
            <Box sx={{ position: "absolute", inset: 0 }} />
          </Tooltip>
        </>
      )}
    </StyledListItem>
  );

  const renderNavigationSection = (section) => {
    const isExpanded = section.expandable
      ? expandedSections[section.key]
      : true;

    return (
      <Box key={section.title} sx={{ 
        mb: 0.5, 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: open ? 'flex-start' : 'center',
      }}>
        {open && section.title && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: '100%',
              px: 2,
              py: 0.5,
              cursor: section.expandable ? "pointer" : "default",
            }}
            onClick={
              section.expandable
                ? (e) => {
                    e.stopPropagation();
                    toggleSection(section.key);
                  }
                : undefined
            }
          >
            <SectionTitle>{section.title}</SectionTitle>
            {section.expandable && (
              <IconButton size="small" sx={{ p: 0.5 }} aria-label={isExpanded ? 'Collapse section' : 'Expand section'}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>
        )}

        <Collapse 
          in={!section.expandable || isExpanded} 
          timeout="auto"
          sx={{ width: '100%' }}
        >
          <List 
            component="div" 
            disablePadding
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: open ? 'flex-start' : 'center',
            }}
          >
            {section.items.map(renderNavigationItem)}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      {/* Enhanced Sidebar */}
      <StyledDrawer variant="permanent" open={open}>
        <StyledDrawerContainer
          open={open}
          sx={{
            "& .MuiCollapse-root": {
              margin: 0,
              padding: 0,
            },
            "& .MuiCollapse-root > div": {
              margin: 0,
              padding: 0,
            },
            "& .MuiList-root": {
              margin: 0,
              padding: 0,
              width: '100%',
            },
            "& .MuiListItem-root": {
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        >
          {/* Navigation Sections */}
          <List sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: open ? 'flex-start' : 'center',
          }}>
            {navigationItems.map(renderNavigationSection)}
          </List>

          {/* Bottom Settings */}
          <Box sx={{ mt: 'auto', width: '100%', 
            borderTop: '1px solid rgba(99,102,241,0.1)', pt: 0.5, pb: 1 }}>
            <Tooltip title={open ? `Right-click for status` : `${user?.email || 'Settings'}`} placement="right">
              <ListItem
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                onContextMenu={(e) => { e.preventDefault(); setStatusAnchor(e.currentTarget); }}
                sx={{
                  width: open ? '90%' : `${collapsedDrawerWidth - 8}px`,
                  margin: '4px auto',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: open ? 'flex-start' : 'center',
                  padding: open ? '7px 10px' : '6px 4px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(99,102,241,0.08)' },
                }}
              >
               <Box display="flex" alignItems="center" gap={1.25}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent=""
                    sx={{
                      '& .MuiBadge-badge': {
                        minWidth: 9, width: 9, height: 9,
                        borderRadius: '50%',
                        bgcolor: myStatusMeta.color,
                        border: '2px solid',
                        borderColor: 'background.paper',
                        p: 0,
                      },
                    }}
                  >
                    <Avatar
                      src={user?.avatar}
                      sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.avatar || theme.palette.primary.light, fontSize: '0.85rem', fontWeight: 700 }}
                    >
                      {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
                    </Avatar>
                  </Badge>
                </Box>
                {open && (
                
                <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }} color="text.primary">
                      {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      {user?.email}
                    </Typography>
                  </Box>

                )}
              </ListItem>
            </Tooltip>

            <Menu
              anchorEl={settingsAnchor}
              open={Boolean(settingsAnchor)}
              onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 180,
                    boxShadow: '0 8px 28px rgba(99,102,241,0.18)',
                    border: '1px solid rgba(99,102,241,0.12)',
                    borderRadius: 2,
                    py: 0,
                    overflow: 'hidden',
                  },
                },
              }}
            >
              {/* User identity header */}
              <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(67,56,202,0.05) 100%)' }}>
                <Box display="flex" alignItems="center" gap={1.25}>
                  <SettingsIcon  fontSize="large" sx={{ color: 'primary.main' }}/>
                  <Box minWidth={0}>
                    <Typography variant="caption" fontSize="1.4rem"  fontWeight={700} sx={{ lineHeight: 1.2 }}>
                      Settings
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Divider />
              <MenuItem
                component={Link}
                to="/settings/?section=account"
                onClick={() => setSettingsAnchor(null)}
                sx={{ gap: 1.5, py: 1, mt: 0.5 }}
              >
                <PersonIcon fontSize="small" />
                <Typography variant="body2" fontWeight={500}>Account</Typography>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/settings/?section=privacy"
                onClick={() => setSettingsAnchor(null)}
                sx={{ gap: 1.5, py: 1 }}
              >
                <PrivacyIcon fontSize="small" />
                <Typography variant="body2" fontWeight={500}>Privacy</Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                component={Link}
                to="/settings/?section=billing"
                onClick={() => setSettingsAnchor(null)}
                sx={{ gap: 1.5, py: 1, mb: 0.5 }}
              >
                <BillingIcon fontSize="small"  />
                <Typography variant="body2" fontWeight={500}>Billing</Typography>
              </MenuItem>
            </Menu>

            {/* Status submenu — right-click on user card */}
            <Menu
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => setStatusAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 190,
                    boxShadow: '0 8px 28px rgba(99,102,241,0.18)',
                    border: '1px solid rgba(99,102,241,0.12)',
                    borderRadius: 2,
                    py: 0,
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(67,56,202,0.05) 100%)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.95rem', color: 'primary.main' }}>
                  Set Status
                </Typography>
              </Box>
              {USER_STATUSES.map(s => (
                <MenuItem
                  key={s.key}
                  onClick={() => { setMyStatus(s.key); setStatusAnchor(null); }}
                  sx={{ gap: 1.5, py: 0.9, bgcolor: myStatus === s.key ? `${s.color}0f` : 'transparent' }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={myStatus === s.key ? 700 : 400} sx={{ color: myStatus === s.key ? s.color : 'text.primary', lineHeight: 1.3 }}>
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.95rem' }}>{s.desc}</Typography>
                  </Box>
                  {myStatus === s.key && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </StyledDrawerContainer>
      </StyledDrawer>
    </>
  );
};

export default Sidebar;